import { useCallback } from "react";
import moment from "moment-timezone";
import { NAVIGATION_UNITS } from "../constants";

interface CalendarInstance {
  navigate: (date: Date) => void;
}

const useCalendarNavigation = (
  instance: CalendarInstance | null,
  selectedDate: Date | null,
  setSelectedDate: (date: Date) => void,
  view: string,
  timezone: string,
) => {
  const navigateToToday = useCallback(() => {
    if (!instance) { return; }
    const today = moment().tz(timezone).toDate();
    setSelectedDate(today);
    requestAnimationFrame(() => instance.navigate(today));
  }, [instance, timezone, setSelectedDate]);

  const navigate = useCallback(
    (direction: "prev" | "next") => {
      if (!instance || !selectedDate) { return; }

      const unit = NAVIGATION_UNITS[view] ?? "days";
      const newDate =
        direction === "prev"
          ? moment(selectedDate).subtract(1, unit).toDate()
          : moment(selectedDate).add(1, unit).toDate();

      setSelectedDate(newDate);
      requestAnimationFrame(() => instance.navigate(newDate));
    },
    [instance, selectedDate, view, setSelectedDate],
  );

  const navigateToPrev = useCallback(() => navigate("prev"), [navigate]);
  const navigateToNext = useCallback(() => navigate("next"), [navigate]);

  return { navigateToToday, navigateToPrev, navigateToNext };
};

export default useCalendarNavigation;
